// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unused-vars */

import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Id } from '@/convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { CopyIcon } from '@/components/ui/icons/copy-icon';
import { Loader2 } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useConfetti } from '@/hooks/useConfetti';
import { PublishSuccessDialog } from '@/components/shared/PublishSuccessDialog';
import { PostAnalytics } from '@/components/shared/PostAnalytics';
import { SwarmCommentsStatus } from '@/components/shared/SwarmCommentsStatus';
import { extractPublishResultSummary } from '@/utils/uploadPostResponses';

interface ContentHistoryItem {
	_id: Id<'contentHistory'>;
	content: string;
	status: string;
	createdAt: string;
	metadata?: {
		buzzUrl?: string;
		templateName?: string;
		postUrl?: string;
		postId?: string;
		conversationId?: string;
		uploadPostRequestId?: string;
		uploadPostJobId?: string;
		uploadPostStatus?: string;
		uploadPostPlatform?: string;
		uploadPostScheduledDate?: string;
		uploadPostResult?: unknown;
		publishedAt?: string;
		swarmPodId?: Id<'engagementPods'>;
		engagement?: {
			likes?: number;
			comments?: number;
			shares?: number;
			views?: number;
		};
	};
	brandVoice?: {
		id: string;
		profileId?: Id<'profiles'>;
	};
}

const LIVE_DELETE_UNAVAILABLE_MESSAGE =
	'Upload Post live post deletion is not confirmed yet. The published social post and local history were left unchanged.';

const UPLOAD_POST_IN_FLIGHT_STATUSES = new Set([
	'pending',
	'queued',
	'processing',
	'in_progress',
]);

const UPLOAD_POST_TERMINAL_STATUSES = new Set([
	'completed',
	'success',
	'failed',
	'error',
	'cancelled',
	'canceled',
]);

function normalizeUploadPostStatus(status?: string) {
	return String(status || '').trim().toLowerCase();
}

const getUploadPostStatus = (item?: ContentHistoryItem | null) =>
	normalizeUploadPostStatus(item?.metadata?.uploadPostStatus);

function isPublishedContentHistoryItem(item?: ContentHistoryItem | null) {
	if (!item) return false;
	const uploadStatus = getUploadPostStatus(item);
	const acceptedUnpublishedUpload =
		(item.metadata?.uploadPostJobId || item.metadata?.uploadPostRequestId) &&
		!item.metadata?.postUrl &&
		!item.metadata?.publishedAt &&
		(uploadStatus === 'scheduled' ||
			UPLOAD_POST_IN_FLIGHT_STATUSES.has(uploadStatus) ||
			Boolean(item.metadata?.uploadPostScheduledDate));
	if (acceptedUnpublishedUpload) return false;
	return (
		item.status === 'published' ||
		!!item.metadata?.postUrl ||
		!!item.metadata?.publishedAt ||
		uploadStatus === 'completed'
	);
}

const isScheduledUploadPostItem = (item?: ContentHistoryItem | null) =>
	Boolean(
		item?.metadata?.uploadPostJobId &&
			!item?.metadata?.postUrl &&
			(getUploadPostStatus(item) === 'scheduled' ||
				(Boolean(item?.metadata?.uploadPostScheduledDate) &&
					!UPLOAD_POST_TERMINAL_STATUSES.has(getUploadPostStatus(item))))
	);

const isInFlightUploadPostItem = (item?: ContentHistoryItem | null) =>
	Boolean(
		(item?.metadata?.uploadPostJobId || item?.metadata?.uploadPostRequestId) &&
			!item?.metadata?.postUrl &&
			UPLOAD_POST_IN_FLIGHT_STATUSES.has(getUploadPostStatus(item))
	);

const isPendingUploadPostItem = (item?: ContentHistoryItem | null) =>
	Boolean(isScheduledUploadPostItem(item) || isInFlightUploadPostItem(item));

export function ContentHistory() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const { user, isSuperAdmin } = useAuth();
	const [showGenerated, setShowGenerated] = useState(true);
	const [showApproved, setShowApproved] = useState(true);
	const [showPublished, setShowPublished] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [dateSearch, setDateSearch] = useState('');
	const [editingItemId, setEditingItemId] =
		React.useState<Id<'contentHistory'> | null>(null);
	const [editedContent, setEditedContent] = React.useState<string>('');
	const [showSaveWarning, setShowSaveWarning] = React.useState(false);
	const [itemToDelete, setItemToDelete] =
		React.useState<Id<'contentHistory'> | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [isPublishing, setIsPublishing] =
		useState<Id<'contentHistory'> | null>(null);
	const [isScheduling, setIsScheduling] =
		useState<Id<'contentHistory'> | null>(null);
	const itemsPerPage = 3;
	const { triggerConfetti, Confetti } = useConfetti();
	const [publishedPostUrl, setPublishedPostUrl] = useState<string>('');
	const [publishedBuzzUrl, setPublishedBuzzUrl] = useState<string>('');
	const [showPublishSuccess, setShowPublishSuccess] = useState(false);
	const [publishedItemTemplateId, setPublishedItemTemplateId] = useState<
		string | undefined
	>();
	const [isDeletingPost, setIsDeletingPost] =
		useState<Id<'contentHistory'> | null>(null);
	const publishPost = useAction(api.publishing.publishPost);
	const deleteUploadPost = useAction(api.uploadPost.deletePost);
	const approveContent = useMutation(api.contentMachine.approveContent);
	const contentMachine = useQuery(api.contentMachine.get, {
		id: id as Id<'contentMachine'>,
		requestingUserId: user?.id as unknown as Id<'users'> | undefined,
	});
	const history = useQuery(api.contentMachine.getContentHistory, {
		machineId: id as Id<'contentMachine'>,
		limit: 50,
	});
	const updateContent = useMutation(api.contentMachine.updateContentHistory);
	const deleteContent = useMutation(api.contentMachine.deleteContentHistory);

	// Create a Map to store unique content items
	const uniqueContentMap = new Map<
		Id<'contentHistory'>,
		ContentHistoryItem
	>();
	if (history?.items) {
		history.items.forEach((item: ContentHistoryItem) => {
			uniqueContentMap.set(item._id, item);
		});
	}

	const filteredHistory = Array.from(uniqueContentMap.values())
		.filter(
				(item: ContentHistoryItem) => {
					const isPublished = isPublishedContentHistoryItem(item);
					const isPending = isPendingUploadPostItem(item);
					const matchesStatus =
						(showGenerated && !isPublished && !isPending) ||
						(showPublished && (isPublished || isPending));
				const matchesSearch =
					searchTerm === '' ||
					item.content
						.toLowerCase()
						.includes(searchTerm.toLowerCase());
				const matchesDate =
					dateSearch === '' ||
					format(
						new Date(item.createdAt)
							.toLocaleString('en-US', {
								timeZone: 'America/Los_Angeles',
							})
							.split(',')[0],
						'yyyy-MM-dd'
					) === dateSearch ||
					(item.metadata?.publishedAt &&
						format(
							new Date(item.metadata.publishedAt)
								.toLocaleString('en-US', {
									timeZone: 'America/Los_Angeles',
								})
								.split(',')[0],
							'yyyy-MM-dd'
						) === dateSearch);

				return matchesStatus && matchesSearch && matchesDate;
			}
		)
		.sort(
			(a, b) =>
				new Date(b.createdAt).getTime() -
				new Date(a.createdAt).getTime()
		);

	// Calculate pagination
	const totalPages = Math.ceil((filteredHistory?.length || 0) / itemsPerPage);
	const currentItems = filteredHistory.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage
	);

	const handleStartEdit = (
		itemId: Id<'contentHistory'>,
		currentContent: string
	) => {
		setEditingItemId(itemId);
		setEditedContent(currentContent);
	};

	const handleSave = async (itemId: Id<'contentHistory'>) => {
		try {
			const item = history?.items?.find(
				(h: { _id: Id<'contentHistory'> }) => h._id === itemId
			);
			await updateContent({
				id: itemId,
				content: editedContent,
				status: 'approved',
				metadata: item?.metadata || undefined,
			});
			setEditingItemId(null);
			setEditedContent('');
			toast({
				title: '✨ Content Updated',
				description: 'Content has been approved and updated',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to update content',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleDelete = async (itemId: Id<'contentHistory'>) => {
		try {
			await deleteContent({
				id: itemId,
			});
			setItemToDelete(null);
			toast({
				title: '🗑️ Content Deleted',
				description: 'Content has been deleted successfully',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to delete content',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleCancelEdit = () => {
		setEditingItemId(null);
		setEditedContent('');
	};

	const handlePublishNow = useCallback(
		async (item: ContentHistoryItem) => {
			try {
				console.log('Content Machine Debug:', {
					contentMachine,
					profileId: contentMachine?.profileId,
					profileKey: contentMachine?.profileKey,
					platform: contentMachine?.platform,
				});

				if (!contentMachine?.profileId || !contentMachine?.profileKey) {
					toast({
						title: 'Error',
						description:
							'No profile connected or profile key missing. Please connect a profile first.',
						variant: 'destructive',
						duration: 3000,
					});
					return;
				}

				setIsPublishing(item._id);
				setShowPublishSuccess(false);
				setPublishedPostUrl('');
				setPublishedBuzzUrl('');
				setPublishedItemTemplateId(undefined);

				const itemToPublish = { ...item };

				const response = await publishPost({
					postText: itemToPublish.content,
					platforms: [contentMachine.platform || ''],
					profileKey: contentMachine.profileKey,
					machineId: contentMachine._id,
					contentHistoryId: itemToPublish._id,
				});

				console.log('Post publish response:', response);

				const publishSummary = extractPublishResultSummary(
					response,
					contentMachine.platform
				);

				if (publishSummary.postUrl) {
					triggerConfetti();
					setPublishedPostUrl(publishSummary.postUrl);
					setPublishedBuzzUrl(itemToPublish.metadata?.buzzUrl || '');
					setPublishedItemTemplateId(item.metadata?.templateName);
					setShowPublishSuccess(true);
				} else if (publishSummary.jobId || publishSummary.requestId) {
					toast({
						title:
							publishSummary.status === 'scheduled'
								? 'Publish Scheduled'
								: 'Still Publishing',
						description:
							publishSummary.message ||
							'Upload Post is still processing this publish. The final published URL will appear in history when processing finishes.',
						duration: 5000,
					});
				} else {
					toast({
						title: 'Publish Submitted',
						description:
							'The publish request completed, but no published URL was returned yet.',
						duration: 5000,
					});
				}
			} catch (error) {
				console.error('Error publishing post:', error);
				if (
					error instanceof Error &&
					error.message.replace(/[\s\S]*Error:\s*/, '').trim() ===
						'duplicate_content'
				) {
					toast({
						title: '⚠️ Duplicate Content Detected',
						description: (
							<div className='space-y-2'>
								<p className='text-sm'>
									This content (or something similar) was
									recently posted.
								</p>
								<div className='mt-2'>
									<p className='text-sm font-medium'>
										Options:
									</p>
									<ul className='text-sm list-none space-y-1 mt-1'>
										<li>
											⏳ Wait 48 hours before reposting
										</li>
										<li>✨ Generate new unique content</li>
										<li>
											✏️ Edit the content to be more
											unique
										</li>
									</ul>
								</div>
							</div>
						),
						variant: 'default',
						duration: 5000,
					});
				} else {
					const errorMessage =
						error instanceof Error
							? error.message
									.replace(/\[.*?\]\s*/g, '')
									.replace(
										/Server Error\s*Uncaught Error:\s*/g,
										''
									)
									.replace(/\s+at\s+handler.*$/gm, '')
									.replace(/\s+Called by client/g, '')
									.replace(/\n/g, '')
									.trim()
							: 'Failed to publish post';
					toast({
						title: 'Error Publishing',
						description: errorMessage,
						variant: 'destructive',
						duration: 3000,
					});
				}
			} finally {
				setIsPublishing(null);
			}
		},
		[
			contentMachine,
			toast,
			publishPost,
			triggerConfetti,
			setPublishedPostUrl,
			setPublishedBuzzUrl,
			setPublishedItemTemplateId,
			setShowPublishSuccess,
			setIsPublishing,
		]
	);

	const handleSchedule = async (item: ContentHistoryItem) => {
		try {
			setIsScheduling(item._id);
			toast({
				title: 'Scheduling Not Enabled',
				description:
					'Upload Post scheduled publishing is not wired into this screen yet.',
				duration: 5000,
			});
		} catch (error) {
			console.error('Error scheduling post:', error);
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to schedule post',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsScheduling(null);
		}
	};

	const truncateUrl = (url: string) => {
		if (url.length > 60) {
			return url.substring(0, 57) + '...';
		}
		return url;
	};

	const getFirstSentence = (text: string) => {
		const match = text.match(/^[^.!?\n]+[.!?\n]/);
		if (match) {
			const sentence = match[0].trim();
			return sentence.length > 100
				? sentence.substring(0, 97) + '...'
				: sentence;
		}
		return text.length > 100 ? text.substring(0, 97) + '...' : text;
	};

	const handleDeletePublishedPost = async (item: ContentHistoryItem) => {
		void item;
		toast({
			title: 'Remote Delete Not Available',
			description: LIVE_DELETE_UNAVAILABLE_MESSAGE,
			variant: 'destructive',
			duration: 6000,
		});
	};

	const handleCancelScheduledPost = async (item: ContentHistoryItem) => {
		const jobId = item.metadata?.uploadPostJobId;
		if (!jobId || !contentMachine?.profileKey) {
			toast({
				title: 'Cannot Cancel Schedule',
				description:
					'This scheduled Upload Post job is missing its job ID or profile key.',
				variant: 'destructive',
				duration: 5000,
			});
			return;
		}

		setIsDeletingPost(item._id);
		try {
			const response = await deleteUploadPost({
				conversationId:
					item.metadata?.conversationId ||
					item.metadata?.uploadPostRequestId ||
					String(item._id),
				profileKey: contentMachine.profileKey,
				jobId,
			});
			const summary = extractPublishResultSummary(response);
			if (!summary.scheduledCancel && summary.status !== 'success') {
				throw new Error(
					summary.message || 'Upload Post did not confirm schedule cancellation'
				);
			}

			await deleteContent({ id: item._id });
			setItemToDelete(null);
			toast({
				title: 'Scheduled Post Cancelled',
				description:
					'Upload Post confirmed the scheduled job was cancelled, and the local history item was removed.',
				duration: 5000,
			});
		} catch (error) {
			toast({
				title: 'Cancel Failed',
				description:
					error instanceof Error
						? error.message
						: 'Failed to cancel scheduled Upload Post job',
				variant: 'destructive',
				duration: 5000,
			});
		} finally {
			setIsDeletingPost(null);
		}
	};

	const handleBlockedInFlightDelete = (item: ContentHistoryItem) => {
		void item;
		toast({
			title: 'Upload Still In Progress',
			description:
				'Upload Post has already accepted this job. Wait for it to publish or fail before deleting the local history item.',
			variant: 'destructive',
			duration: 6000,
		});
	};

	useEffect(() => {
		const handlePublishContent = async () => {
			console.log('🎯 Received publish-content event');

				// Find the latest unpublished content
				const unpublishedItems =
					history?.items?.filter(
						(item: ContentHistoryItem) =>
							!isPublishedContentHistoryItem(item) &&
							!isPendingUploadPostItem(item)
					) || [];
			const latestItem = unpublishedItems[0]; // Items are already sorted by date desc

			if (latestItem) {
				console.log('🚀 Publishing latest content:', latestItem);
				await handlePublishNow(latestItem as ContentHistoryItem);
			} else {
				console.log('No unpublished content found to publish');
				toast({
					title: 'No Content to Publish',
					description: 'There is no unpublished content available.',
					variant: 'destructive',
					duration: 3000,
				});
			}
		};

		document.addEventListener(
			'trigger-content-publishing',
			handlePublishContent
		);
		return () => {
			document.removeEventListener(
				'trigger-content-publishing',
				handlePublishContent
			);
		};
	}, [history?.items, handlePublishNow, toast]);

	React.useEffect(() => {
		if (history?.items) {
			console.log('Content History Items:', history.items);
			console.log('Content Machine:', contentMachine);

				// Log details of items with postIds
				const publishedItems = history.items.filter(
					(item: ContentHistoryItem) => isPublishedContentHistoryItem(item)
				);
			console.log(
				'Published items with postIds:',
				publishedItems.map((item: ContentHistoryItem) => ({
					postId: item.metadata?.postId,
					postUrl: item.metadata?.postUrl,
					publishedAt: item.metadata?.publishedAt,
					platform: contentMachine?.platform,
				}))
			);
		}
	}, [history?.items, contentMachine]);

	const pendingDeleteItem = history?.items?.find(
		(item: ContentHistoryItem) => item._id === itemToDelete
	) as ContentHistoryItem | undefined;

	return (
		<div className='container mx-auto p-6 pb-80'>
			<div className='flex justify-between items-center mb-6'>
				<div>
					<h1 className='text-3xl font-bold py-4 bg-gradient-to-b from-pink-400 to-pink-600 text-transparent bg-clip-text'>
						Content History
					</h1>
					<p className='text-lg text-gray-600 dark:text-gray-400 -mt-2 mb-4'>
						View and manage your content history for{' '}
						<span className='text-gray-900 dark:text-white font-bold'>
							{contentMachine?.name || 'Content Machine'}
						</span>
					</p>
				</div>
				<Button
					variant='outline'
					onClick={() => navigate(`/admin/content-machine/${id}`)}
					className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white h-fit'
				>
					← Back to Content Machine
				</Button>
			</div>

			<Card className='glow-card'>
				<CardContent className='p-6'>
					<div className='flex items-center gap-4 mb-6'>
						<div className='flex items-center gap-4 whitespace-nowrap'>
							<Badge
								variant='outline'
								className={cn(
									'px-3 py-1 cursor-pointer transition-all duration-200',
									showGenerated
										? 'bg-gradient-to-b from-yellow-400 to-orange-600 text-white'
										: 'bg-gradient-to-b from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 hover:from-yellow-100 hover:to-yellow-200 dark:hover:from-yellow-900/30 dark:hover:to-yellow-800/30'
								)}
								onClick={() => setShowGenerated(!showGenerated)}
							>
								💾 Saved
							</Badge>
							<Badge
								variant='outline'
								className={cn(
									'px-3 py-1 cursor-pointer transition-all duration-200',
									showPublished
										? 'bg-gradient-to-b from-pink-400 to-pink-600 text-white'
										: 'bg-gradient-to-b from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 hover:from-pink-100 hover:to-pink-200 dark:hover:from-pink-900/30 dark:hover:to-pink-800/30'
								)}
								onClick={() => setShowPublished(!showPublished)}
							>
								🚀 Published
							</Badge>
						</div>

						<Input
							placeholder='Search content...'
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className='max-w-xs'
						/>
						<Input
							type='date'
							value={dateSearch}
							onChange={(e) => {
								const date = e.target.value;
								// Convert to local timezone
								if (date) {
									const localDate = new Date(date);
									localDate.setMinutes(
										localDate.getMinutes() +
											localDate.getTimezoneOffset()
									);
									setDateSearch(
										format(localDate, 'yyyy-MM-dd')
									);
								} else {
									setDateSearch('');
								}
							}}
							className='max-w-xs'
						/>
					</div>

					<div className='space-y-12'>
						{currentItems.length > 0 ? (
							currentItems.map((item: ContentHistoryItem) => (
								<Card key={item._id} className='p-10'>
									<div className='space-y-8'>
										<div className='flex flex-col gap-6'>
											<Badge
												variant='outline'
												className='w-fit text-lg px-6 py-2 bg-gradient-to-b from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800 text-white border-none shadow-lg'
											>
												{contentMachine?.platform ===
													'twitterx' &&
													'X Content Machine'}
												{contentMachine?.platform ===
													'linkedin' &&
													'💼 LinkedIn Content Machine'}
												{(!contentMachine?.platform ||
													(contentMachine.platform !==
														'twitterx' &&
														contentMachine.platform !==
															'linkedin')) &&
													'✨ Content Machine'}
											</Badge>
											<h3 className='text-4xl font-bold bg-gradient-to-b from-pink-400 to-pink-600 text-transparent bg-clip-text mb-6 leading-tight tracking-tight'>
												{getFirstSentence(item.content)}
											</h3>
											<div className='flex items-center gap-2'>
													<Badge
														variant='outline'
														className={cn(
															'px-3 py-1',
															isPublishedContentHistoryItem(item)
																? 'bg-gradient-to-b from-pink-400 to-pink-600 text-white'
																: isPendingUploadPostItem(item)
																	? 'bg-gradient-to-b from-blue-400 to-blue-600 text-white'
																: 'bg-gradient-to-b from-yellow-400 to-yellow-600 text-white'
														)}
													>
														{isPublishedContentHistoryItem(item)
															? '🚀 Published'
															: isPendingUploadPostItem(item)
																? '🔄 Publishing'
																: '💾 Saved'}
													</Badge>
												{item.brandVoice?.id && (
													<Badge
														variant='outline'
														className='bg-gradient-to-b from-purple-400 to-purple-600 text-white px-3 py-1'
													>
														{item.brandVoice.id ===
															'prometheus' &&
															'🔥 Prometheus'}
														{item.brandVoice.id ===
															'reverend' &&
															'🎭 The Reverend'}
														{item.brandVoice.id ===
															'brent' &&
															'💼 Brent Ecomm Master'}
														{item.brandVoice.id ===
															'user' &&
															'✨ My Brand Voice'}
													</Badge>
												)}
												{item.metadata?.templateName ? (
													<Badge
														variant='outline'
														className='bg-gradient-to-b from-blue-400 to-blue-600 text-white px-3 py-1'
													>
														📝{' '}
														{
															item.metadata
																.templateName
														}
													</Badge>
												) : (
													<Badge
														variant='outline'
														className='bg-gradient-to-b from-gray-400 to-gray-600 text-white px-3 py-1'
													>
														🎯 No Template
													</Badge>
												)}
											</div>
											<div className='flex flex-col gap-2 text-base'>
												<div>
													<div className='text-gray-600 dark:text-blue-400'>
														<span className='font-bold text-gray-900 dark:text-gray-200'>
															Created:{' '}
														</span>
														{new Date(
															item.createdAt
														).toLocaleString()}
													</div>
													{item.metadata
														?.publishedAt && (
														<div className='text-gray-600 dark:text-blue-400'>
															<span className='font-bold text-gray-900 dark:text-gray-200'>
																Published:{' '}
															</span>
															{new Date(
																item.metadata.publishedAt
															).toLocaleString()}
														</div>
													)}
												</div>
												<div className='mt-4 space-y-2'>
													{item.metadata?.buzzUrl && (
														<div className='text-gray-600 dark:text-blue-400 whitespace-nowrap overflow-hidden text-ellipsis max-w-[600px]'>
															<span className='font-bold text-gray-900 dark:text-gray-200'>
																Buzz Source:{' '}
															</span>
															<a
																href={
																	item
																		.metadata
																		.buzzUrl
																}
																target='_blank'
																rel='noopener noreferrer'
																className='hover:text-blue-600 dark:hover:text-blue-300'
															>
																{
																	item
																		.metadata
																		.buzzUrl
																}
															</a>
														</div>
													)}
													{item.metadata?.postUrl && (
														<div className='text-gray-600 dark:text-blue-400 whitespace-nowrap overflow-hidden text-ellipsis max-w-[600px]'>
															<span className='font-bold text-gray-900 dark:text-gray-200'>
																Published URL:{' '}
															</span>
															<a
																href={
																	item
																		.metadata
																		.postUrl
																}
																target='_blank'
																rel='noopener noreferrer'
																className='hover:text-blue-600 dark:hover:text-blue-300'
															>
																{
																	item
																		.metadata
																		.postUrl
																}
															</a>
														</div>
													)}
												</div>
											</div>
										</div>
										<div className='whitespace-pre-wrap'>
											{editingItemId === item._id ? (
												<Textarea
													value={editedContent}
													onChange={(e) =>
														setEditedContent(
															e.target.value
														)
													}
													className='min-h-[400px] resize-y text-xl leading-relaxed p-6'
												/>
											) : (
												<div className='text-xl leading-relaxed'>
													{item.content}
												</div>
											)}
										</div>
										{item.metadata?.postId &&
											contentMachine?.profileKey && (
												<div className='mt-6'>
													<h4 className='text-lg font-semibold bg-gradient-to-r from-pink-400 to-pink-600 text-transparent bg-clip-text mb-4'>
														Post Analytics:
													</h4>
														<PostAnalytics
															postId={
																item.metadata.postId
															}
															requestId={
																item.metadata
																	.uploadPostRequestId
															}
															platform={
																contentMachine.platform ===
															'twitterx'
																? 'twitter'
																: contentMachine.platform
														}
														profileKey={
															contentMachine.profileKey
														}
														contentHistoryId={
															item._id
														}
														className='mt-4'
													/>
												</div>
											)}
										{item.metadata?.swarmPodId && (
											<SwarmCommentsStatus
												contentHistoryId={item._id}
												podId={
													item.metadata
														.swarmPodId as Id<'engagementPods'>
												}
												className='mt-8'
											/>
										)}
										<div className='flex flex-col gap-4 pt-8 mt-8 border-t border-gray-800'>
											<div className='flex items-center gap-2 justify-end'>
												{editingItemId === item._id ? (
													<>
														<Button
															variant='outline'
															size='sm'
															onClick={
																handleCancelEdit
															}
															className='bg-gradient-to-b from-gray-400 to-zinc-600 hover:from-gray-500 hover:to-zinc-700 text-white'
														>
															Cancel
														</Button>
														<Button
															variant='default'
															size='sm'
															onClick={() =>
																handleSave(
																	item._id
																)
															}
															className='bg-gradient-to-b from-emerald-400 to-green-600 hover:from-emerald-500 hover:to-green-700 text-white'
														>
															Save
														</Button>
													</>
												) : (
													<>
															{!isPublishedContentHistoryItem(
																item
															) ? (
															<>
																<Button
																	variant='outline'
																	size='sm'
																	onClick={() =>
																		handleStartEdit(
																			item._id,
																			item.content
																		)
																	}
																	disabled={
																		isPublishing ===
																			item._id ||
																		isDeletingPost ===
																			item._id ||
																		isScheduledUploadPostItem(
																			item
																		) ||
																		isInFlightUploadPostItem(
																			item
																		)
																	}
																	className='bg-gradient-to-b from-sky-400 to-blue-600 hover:from-sky-500 hover:to-blue-700 text-white'
																>
																	✏️ Edit
																</Button>
																<Button
																	variant='destructive'
																	size='sm'
																	onClick={() => {
																		if (
																			isInFlightUploadPostItem(
																				item
																			)
																		) {
																			handleBlockedInFlightDelete(
																				item
																			);
																			return;
																		}
																		setItemToDelete(
																			item._id
																		);
																	}}
																	disabled={
																		isPublishing ===
																			item._id ||
																		isDeletingPost ===
																			item._id
																	}
																	className='bg-gradient-to-b from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white'
																>
																	{isDeletingPost ===
																	item._id ? (
																		<>
																			<Loader2 className='h-4 w-4 animate-spin mr-2' />
																			Deleting...
																		</>
																	) : isScheduledUploadPostItem(
																			item
																	  ) ? (
																		'Cancel Scheduled Post'
																	) : isInFlightUploadPostItem(
																			item
																	  ) ? (
																		'Upload In Progress'
																	) : (
																		'🗑️ Delete Content'
																	)}
																</Button>
															</>
														) : (
															<>
																<Button
																	variant='outline'
																	size='sm'
																	onClick={() => {
																		navigator.clipboard.writeText(
																			item.content
																		);
																		toast({
																			title: '🔥 Content Copied 🔥',
																			description:
																				'The content has been copied to your clipboard.',
																			duration: 3000,
																		});
																	}}
																	disabled={
																		isPublishing ===
																			item._id ||
																		isDeletingPost ===
																			item._id
																	}
																	className='bg-gradient-to-b from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white group'
																>
																	<CopyIcon className='mr-2 h-4 w-4 group-hover:rotate-12 transition-transform duration-200' />
																	Copy
																</Button>
																	<Button
																		variant='destructive'
																		size='sm'
																		onClick={() =>
																			setItemToDelete(
																				item._id
																			)
																		}
																	disabled={
																		isDeletingPost ===
																		item._id
																	}
																	className='bg-gradient-to-b from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white'
																>
																	{isDeletingPost ===
																	item._id ? (
																		<>
																			<Loader2 className='h-4 w-4 animate-spin mr-2' />
																			Deleting...
																		</>
																	) : (
																		'Remote Delete Blocked'
																	)}
																</Button>
															</>
														)}
													</>
												)}
											</div>
												{isPendingUploadPostItem(item) && (
													<div className='mt-6 p-4 border border-blue-300 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-950/30 text-sm text-blue-900 dark:text-blue-100'>
														<div className='font-semibold'>
															Upload Post is processing this publish job.
														</div>
														<div className='mt-1 text-blue-700 dark:text-blue-200'>
															Status:{' '}
															{item.metadata?.uploadPostStatus ||
																'pending'}
																{item.metadata?.uploadPostJobId
																	? ` - Job ${item.metadata.uploadPostJobId}`
																	: item.metadata?.uploadPostRequestId
																		? ` - Request ${item.metadata.uploadPostRequestId}`
																		: ''}
														</div>
													</div>
												)}
												{!isPublishedContentHistoryItem(item) &&
													!isPendingUploadPostItem(item) && (
													<>
														<div className='mt-6 p-4 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50'>
														<div className='flex items-center gap-3'>
															<div className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white'>
																🐝
															</div>
															<h3 className='text-lg font-semibold'>
																<span className='bg-gradient-to-r from-pink-400 to-pink-600 text-transparent bg-clip-text'>
																	SWARM Pod
																	Status
																</span>
															</h3>
														</div>
														<p className='mt-3 text-gray-600 dark:text-gray-400'>
															Publish your content
															to enable SWARM Pod
															engagement. After
															publishing, you'll
															be able to manage
															and monitor comment
															activities from your
															pod members.
														</p>
													</div>
													<div className='flex items-center gap-4 justify-end mt-8 pt-8 border-t border-gray-800'>
														<Button
															variant='outline'
															size='lg'
															onClick={() =>
																handlePublishNow(
																	item as ContentHistoryItem
																)
															}
															disabled={
																isPublishing ===
																item._id
															}
															className={cn(
																'bg-gradient-to-r from-fuchsia-500 via-purple-500 to-pink-500 text-white text-lg py-8 px-12 transition-all duration-300',
																!isPublishing &&
																	'hover:from-fuchsia-600 hover:via-purple-600 hover:to-pink-600 hover:scale-105',
																'shadow-[0_0_30px_rgba(217,70,219,0.5)] hover:shadow-[0_0_50px_rgba(217,70,219,0.6)]',
																isPublishing ===
																	item._id &&
																	'opacity-70'
															)}
														>
															<div className='flex items-center justify-center gap-2'>
																{isPublishing ===
																item._id ? (
																	<>
																		<Loader2 className='h-6 w-6 animate-spin' />
																		<span>
																			Publishing...
																		</span>
																	</>
																) : (
																	'🚀 Publish Now'
																)}
															</div>
														</Button>
														<Button
															variant='outline'
															size='lg'
															onClick={() =>
																handleSchedule(
																	item as ContentHistoryItem
																)
															}
																disabled
															className={cn(
																'bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 text-white text-lg py-8 px-12 transition-all duration-300',
																!isPublishing &&
																	!isScheduling &&
																	'hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-600 hover:scale-105',
																'shadow-[0_0_30px_rgba(59,130,246,0.5)] hover:shadow-[0_0_50px_rgba(59,130,246,0.6)]',
																(isPublishing ===
																	item._id ||
																	isScheduling ===
																		item._id) &&
																	'opacity-70'
															)}
														>
															<div className='flex items-center justify-center gap-2'>
																{isScheduling ===
																item._id ? (
																	<>
																		<Loader2 className='h-6 w-6 animate-spin' />
																		<span>
																			Scheduling...
																		</span>
																	</>
																) : (
																	'Schedule Unavailable'
																)}
															</div>
															</Button>
														</div>
													</>
												)}
										</div>
									</div>
								</Card>
							))
						) : (
							<Card className='p-10'>
								<div className='flex flex-col items-center justify-center gap-6 py-12 text-center'>
									<div className='text-6xl animate-bounce'>
										{dateSearch ? '🗓️' : '🔍'}
									</div>
									<h3 className='text-2xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 text-transparent bg-clip-text'>
										{dateSearch ? (
											<>
												No content found for{' '}
												{dateSearch}
												<div className='text-lg font-normal text-gray-600 dark:text-gray-400 mt-2'>
													Try another date or check if
													you have any content for
													this day!
													<br />
													<span className='text-sm'>
														(Psst! The date should
														match exactly -{' '}
														{format(
															new Date(),
															'yyyy-MM-dd'
														)}{' '}
														for today)
													</span>
												</div>
											</>
										) : (
											'No content found'
										)}
									</h3>
								</div>
							</Card>
						)}
					</div>

					{totalPages > 1 && (
						<div className='flex flex-col items-center justify-center gap-4 mt-8'>
							<div className='flex items-center gap-4'>
								<Button
									variant='outline'
									size='sm'
									onClick={() => setCurrentPage(1)}
									disabled={currentPage === 1}
									className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
								>
									⏮️ First
								</Button>
								<Button
									variant='outline'
									size='sm'
									onClick={() =>
										setCurrentPage(currentPage - 1)
									}
									disabled={currentPage === 1}
									className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
								>
									← Previous
								</Button>
								<div className='flex items-center gap-2'>
									{Array.from(
										{ length: Math.min(totalPages, 5) },
										(_, i: number) => {
											const pageNum = i + 1;
											return (
												<Button
													key={pageNum}
													variant='outline'
													size='sm'
													onClick={() =>
														setCurrentPage(pageNum)
													}
													className={cn(
														'w-10 h-10',
														currentPage === pageNum
															? 'bg-gradient-to-b from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white'
															: 'bg-gradient-to-b from-gray-400 to-gray-600 hover:from-gray-500 hover:to-gray-700 text-white'
													)}
												>
													{pageNum}
												</Button>
											);
										}
									)}
								</div>
								<Button
									variant='outline'
									size='sm'
									onClick={() =>
										setCurrentPage(currentPage + 1)
									}
									disabled={currentPage === totalPages}
									className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
								>
									Next →
								</Button>
								<Button
									variant='outline'
									size='sm'
									onClick={() => setCurrentPage(totalPages)}
									disabled={currentPage === totalPages}
									className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
								>
									Last ⏭️
								</Button>
							</div>
							<div className='text-sm text-gray-400 text-center'>
								<p>
									Showing items{' '}
									{currentPage * itemsPerPage -
										itemsPerPage +
										1}{' '}
									to{' '}
									{Math.min(
										currentPage * itemsPerPage,
										filteredHistory.length
									)}{' '}
									of {filteredHistory.length} total items
								</p>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			<ConfirmDialog
				isOpen={showSaveWarning}
				onClose={() => setShowSaveWarning(false)}
				title='Save Changes'
				description='Are you sure you want to save these changes? This will update the content history.'
				onConfirm={async () => {
					if (editingItemId) {
						await handleSave(editingItemId);
					}
					setShowSaveWarning(false);
				}}
				confirmText='Save Changes'
				confirmButtonClassName='bg-gradient-to-b from-emerald-400 to-green-600 hover:from-emerald-500 hover:to-green-700 text-white'
			/>

			<ConfirmDialog
				isOpen={itemToDelete !== null}
				onClose={() => setItemToDelete(null)}
				title={
					<div className='flex flex-col items-center justify-center py-8 space-y-4'>
						<img
							src='/logo.png'
							alt='Prometheus'
							className='h-12 mb-4'
						/>
						<motion.div
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							className='text-xl font-semibold text-purple-500'
						>
								{isPublishedContentHistoryItem(pendingDeleteItem)
									? 'Remote Delete Blocked'
								: isScheduledUploadPostItem(pendingDeleteItem)
									? 'Cancel Scheduled Post'
									: 'Delete Content History'}
						</motion.div>
						<div className='text-sm text-gray-400 text-center'>
								{isPublishedContentHistoryItem(pendingDeleteItem)
									? LIVE_DELETE_UNAVAILABLE_MESSAGE
								: isScheduledUploadPostItem(pendingDeleteItem)
									? 'This will cancel the scheduled Upload Post job before removing the local history item.'
									: '✨ Are you sure you want to delete this content? ✨'}
						</div>
					</div>
				}
				description={
					<div className='p-4 bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 rounded-lg shadow-inner'>
						<p className='text-lg text-zinc-400 dark:text-zinc-400 text-center'>
								{isPublishedContentHistoryItem(pendingDeleteItem) ? (
								<>
									Upload Post live deletion has not been
									confirmed, so this action is blocked.
									<br />
									The published social post and local record
									will remain unchanged.
								</>
							) : isScheduledUploadPostItem(
									pendingDeleteItem
							  ) ? (
								<>
									Upload Post will be asked to cancel the
									scheduled job first.
									<br />
									The local content history item will only be
									removed after cancellation succeeds.
								</>
							) : (
								<>
									⚡️ This will permanently remove this
									content
									<br />
									from your content history.
									<br />
									This action cannot be undone.⚡️
								</>
							)}
						</p>
					</div>
				}
				onConfirm={async () => {
					if (itemToDelete) {
						const item = pendingDeleteItem;
							if (isPublishedContentHistoryItem(item)) {
							await handleDeletePublishedPost(item);
						} else if (isScheduledUploadPostItem(item)) {
							await handleCancelScheduledPost(item);
						} else if (isInFlightUploadPostItem(item)) {
							handleBlockedInFlightDelete(item);
						} else {
							await handleDelete(itemToDelete);
						}
						setItemToDelete(null);
					}
				}}
					confirmText={
						isPublishedContentHistoryItem(pendingDeleteItem)
							? 'Close'
							: isScheduledUploadPostItem(pendingDeleteItem)
								? 'Cancel Schedule'
								: '🗑️ Delete'
					}
					confirmButtonClassName={
						isPublishedContentHistoryItem(pendingDeleteItem)
							? 'bg-gradient-to-b from-zinc-500 to-zinc-700 hover:from-zinc-600 hover:to-zinc-800 text-white shadow-lg hover:shadow-xl transition-all duration-200'
							: 'hover:from-red-700 hover:to-red-900 text-white shadow-lg hover:shadow-xl transition-all duration-200'
					}
			/>
			<Confetti />

			<PublishSuccessDialog
				isOpen={showPublishSuccess}
				onClose={() => setShowPublishSuccess(false)}
				postUrl={publishedPostUrl}
				templateName={publishedItemTemplateId}
				machineId={contentMachine?._id.toString() ?? ''}
				hideHistoryButton={true}
				buzzUrl={publishedBuzzUrl}
			/>
		</div>
	);
}
